import React from 'react';
import { useTranslation } from 'react-i18next';
import { type NavItemNoIcon, type SiteConfig, variant } from '@cx-web/domain-types';

import { useMenuOverflow } from '../../hooks/use-menu-overflow';
import { HeaderNavItem } from '../HeaderNavItem/index';
import MoreDropdown from '../MoreDropdown/index';

import styles from './PrimaryHeaderLeftMenuItems.module.css';

type PrimaryHeaderLeftMenuItemsProps = {
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  links: NavItemNoIcon[];
  serverUrl: string;
  siteConfig: SiteConfig;
};

// First-level (primary) left menu items: renders the items that
// fit the container width and collapses the rest into MoreDropdown. Overflow is measured against a
// hidden, full-width copy of the list (see useMenuOverflow); pre-JS the visible row simply clips at the
// container edge (no overlap, no More button) until the hook collapses the overflow on hydrate.
const PrimaryHeaderLeftMenuItems = ({ dropdownRef, links, serverUrl, siteConfig }: PrimaryHeaderLeftMenuItemsProps) => {
  const { t } = useTranslation(['label']);
  const { containerRef, dropdownItems, mainItems, measureRef, showMoreDropdown } = useMenuOverflow(links);

  return (
    <div className={styles.wrapper} data-testid="primary-header-left-menu" ref={containerRef}>
      <ul className={styles.navItems} data-testid="primary-header-nav-items">
        {mainItems.map((link, index) => (
          <HeaderNavItem
            {...link}
            bookmakers={siteConfig.bookmakers}
            dropdownRef={dropdownRef}
            isUkStyle
            key={`${link.link}-${index}`}
            serverUrl={serverUrl}
            variant={variant.primary}
          />
        ))}
      </ul>

      {showMoreDropdown && <MoreDropdown items={dropdownItems} serverUrl={serverUrl} />}

      {/* Hidden measurement copy of the full list + the More trigger — read for widths, never seen.
          visibility:hidden keeps it out of the tab order and the accessibility tree; dropdownRef is
          passed so expandable items measure at the same width as the visible row. */}
      <div className={styles.measureClip}>
        <ul aria-hidden className={styles.measure} data-testid="primary-header-nav-measure" ref={measureRef}>
          {links.map((link, index) => (
            <HeaderNavItem
              {...link}
              bookmakers={siteConfig.bookmakers}
              dropdownRef={dropdownRef}
              isUkStyle
              key={`${link.link}-${index}`}
              serverUrl={serverUrl}
              variant={variant.primary}
            />
          ))}
          <li className={styles.measureMore}>{t('RESPONSIVE_MORE')}</li>
        </ul>
      </div>
    </div>
  );
};

export { PrimaryHeaderLeftMenuItems };
